import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import ScrollContainer from 'react-indiana-drag-scroll';

import { SearchInput } from './components/SearchInput';
import { HoursDisplay } from '../components/hours-display';
import { FooterSpacer } from '../components/footer-spacer';
import { ROSTER_DAILY_CONFIG } from './roster-daily-config';
import { WorkersTimeline } from './components/WorkersTimeline';
import { CustomersSideColumn } from './components/CustomersSideColumn';
import { WorkersSideColumn } from './components/WorkersSideColumn';
import { CustomersTimeline } from './components/CustomersTimeline';
import { generateHours, uniqueCustomers, uniqueWorkers } from '../components/roster-utilities';
import {
  IRosterBooking,
  IRosterCustomer,
  IRosterCustomerWithBookings,
  IRosterShift,
  IRosterWorker,
  IRosterWorkerWithShifts,
} from '../types';
import { getEarliestSlot, groupAssignedShifts, groupBookings, groupUnassignedShifts } from './utils';
import { OnAddBooking } from './components/add-booking-popover';

const { CONTENT_PANEL_WIDTH, HOUR_BLOCK_HEIGHT } = ROSTER_DAILY_CONFIG;

/*
  Some notes about the display mode properties :
   Display Mode :
   • Dynamic - list of customers/workers will be derived from the bookings/shifts (unique customers/workers).
   • Fixed   - list of customers/workers will be displayed based on the list of customers/workers passed in as props.
 */
interface RosterTimelineProps {
  bookings: IRosterBooking[];
  assignedShifts: IRosterShift[];
  unassignedShifts: IRosterShift[];
  selectedDate: Date;
  showOpenSlots: boolean;
  showCustomers: boolean;
  showWorkers: boolean;
  customersList?: IRosterCustomer[];
  customersDisplayMode?: 'dynamic' | 'fixed';
  workersList?: IRosterWorker[];
  workersDisplayMode?: 'dynamic' | 'fixed';
  refreshData: () => void;
  onAddBooking: OnAddBooking;
}

function RosterDaily({
  bookings,
  unassignedShifts = [],
  assignedShifts = [],
  selectedDate,
  customersList,
  workersList,
  showOpenSlots = true,
  showCustomers = true,
  showWorkers = true,
  refreshData,
  customersDisplayMode = 'dynamic',
  workersDisplayMode = 'dynamic',
  onAddBooking,
}: RosterTimelineProps) {
  const [isCustomersOpen, setCustomersOpen] = useState(true);
  const [isWorkersOpen, setWorkersOpen] = useState(true);
  const scrollContainerRef = useRef<ScrollContainer | null>(null);
  const hoursToDisplay = useMemo(() => generateHours(0, 23), []);
  const publishedUnfilledShifts = useMemo(
    () =>
      _.filter(
        assignedShifts,
        (shift) =>
          _.isEmpty(shift.supportWorkerId) &&
          (shift.shiftSlotStatus === 'PUBLISHING' || shift.shiftSlotStatus === 'PUBLISHED'),
      ),
    [assignedShifts],
  );

  // Combined unassigned and published unfilled shifts for display
  const effectiveUnassignedShifts = useMemo(
    () => [...unassignedShifts, ...publishedUnfilledShifts],
    [unassignedShifts, publishedUnfilledShifts],
  );

  const combinedShifts = showOpenSlots ? [...effectiveUnassignedShifts, ...assignedShifts] : [...assignedShifts];

  // Display mode = 'fixed'   -> use the passed in customers list.
  // Display mode = 'dynamic' -> get unique list of customers/workers in the bookings/shifts
  const customers = useMemo(
    () => (customersDisplayMode === 'fixed' ? (customersList ?? []) : uniqueCustomers(bookings)),
    [bookings, customersList, customersDisplayMode],
  );
  const workers = useMemo(
    () => (workersDisplayMode === 'fixed' ? (workersList ?? []) : uniqueWorkers(combinedShifts)),
    [combinedShifts, workersList, workersDisplayMode],
  );

  // OVERLAP CHECKS & GROUPINGS, The following functions checks for overlaps in shifts, and groups them into multiple rows to avoid overlaps. Open slots
  const unassignedWorkerShifts: IRosterShift[][] = useMemo(
    () => groupUnassignedShifts(unassignedShifts),
    [unassignedShifts],
  );

  // Assigned shifts
  const assignedWorkerShifts: IRosterWorkerWithShifts[] = useMemo(
    () => groupAssignedShifts(workers, assignedShifts),
    [workers, assignedShifts],
  );

  // Bookings
  const customerBookings: IRosterCustomerWithBookings[] = useMemo(
    () => groupBookings(customers, bookings),
    [customers, bookings],
  );

  // Where should the timeline begin at; offset in hours. Default is 8am
  let scrollToHour = 8;

  // Minimum date for the entire shift/booking. Commented out because it's a weird experience.
  const earliestSlot = getEarliestSlot(unassignedShifts, assignedShifts, bookings);

  // If there are any earliest slots found, scroll to the target hour.
  if (!_.isEmpty(earliestSlot)) {
    scrollToHour = moment(earliestSlot.startDateTime).hour() - 1;
  }

  // Initial scroll
  useEffect(() => {
    if (!_.isEmpty(scrollContainerRef) && !_.isEmpty(scrollContainerRef.current)) {
      const element = scrollContainerRef.current.getElement();
      if (!_.isEmpty(element)) {
        element.scrollTo({ left: 120 * scrollToHour });
      }
    }
  }, [scrollToHour]);

  return (
    <div className='select-none  bordered-bottom'>
      <div className='flex-row' id='main-container'>
        <div style={{ width: `${CONTENT_PANEL_WIDTH}px`, minHeight: '1vh' }} className='bg-white' id='left-container'>
          <SearchInput height={HOUR_BLOCK_HEIGHT} />
          {showCustomers && (
            <CustomersSideColumn
              customers={customers}
              isCustomersOpen={isCustomersOpen}
              setCustomersOpen={setCustomersOpen}
              bookings={customerBookings}
            />
          )}
          {showWorkers && (
            <WorkersSideColumn
              assignedShifts={assignedWorkerShifts}
              unassignedShifts={unassignedWorkerShifts}
              workers={workers}
              isWorkersOpen={isWorkersOpen}
              setWorkersOpen={setWorkersOpen}
            />
          )}
          <FooterSpacer width={CONTENT_PANEL_WIDTH} />
        </div>
        <ScrollContainer
          className='bg-tertiary cursor-all-scroll'
          style={{ width: `calc(100vw - ${CONTENT_PANEL_WIDTH}px)` }}
          hideScrollbars={false}
          vertical={false}
          ref={scrollContainerRef}
        >
          <HoursDisplay hours={hoursToDisplay} />
          {showCustomers && (
            <CustomersTimeline
              hours={hoursToDisplay}
              customers={customers}
              bookings={customerBookings}
              isCustomersOpen={isCustomersOpen}
              currentDate={selectedDate}
              refreshData={refreshData}
              onAddBooking={onAddBooking}
            />
          )}
          {showWorkers && (
            <WorkersTimeline
              hours={hoursToDisplay}
              assignedShifts={assignedWorkerShifts}
              unassignedShifts={unassignedWorkerShifts}
              workers={workers}
              isWorkersOpen={isWorkersOpen}
              refreshData={refreshData}
            />
          )}
        </ScrollContainer>
      </div>
    </div>
  );
}

export default RosterDaily;
